import type { AgendaFilterOverride } from '@conference/shared';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';

export function AgendaFilterEditor({ filters, onChange, onReset }: {
  filters: AgendaFilterOverride[];
  onChange: (filters: AgendaFilterOverride[]) => void;
  onReset: () => void;
}) {
  function patch(index: number, edit: Partial<AgendaFilterOverride>) {
    onChange(filters.map((filter, position) => position === index ? { ...filter, ...edit } : { ...filter }));
  }

  function move(index: number, offset: -1 | 1) {
    const destination = index + offset;
    if (destination < 0 || destination >= filters.length) return;
    const next = filters.map((filter) => ({ ...filter }));
    [next[index], next[destination]] = [next[destination], next[index]];
    onChange(next.map((filter, position) => ({ ...filter, order: (position + 1) * 10 })));
  }

  return <div className="mt-4 space-y-2 rounded-lg border border-gray-800 bg-gray-950/60 p-3">
    <div className="flex items-center justify-between">
      <div><h3 className="text-xs font-semibold text-gray-200">Agenda filters</h3><p className="text-[11px] text-gray-500">All is always shown. Customize filters derived from uploaded sessions.</p></div>
      <button type="button" aria-label="Reset agenda filters" onClick={onReset} className="rounded p-1 text-gray-400 hover:text-white"><RotateCcw size={14} /></button>
    </div>
    {filters.length === 0 && <p className="text-xs text-gray-500">Upload sessions to configure their types.</p>}
    {filters.map((filter, index) => <div key={filter.type} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded border border-gray-800 p-2">
      <input
        type="checkbox"
        aria-label={`Show ${filter.type} filter`}
        checked={filter.enabled !== false}
        onChange={(event) => patch(index, { enabled: event.target.checked })}
      />
      <label className="text-[11px] text-gray-500">{filter.type}
        <input
          aria-label={`${filter.type} filter label`}
          value={filter.label ?? ''}
          onChange={(event) => patch(index, { label: event.target.value })}
          className="mt-1 block w-full rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-white"
        />
      </label>
      <div className="flex gap-1">
        <button type="button" aria-label={`Move ${filter.type} filter up`} disabled={index === 0} onClick={() => move(index, -1)} className="rounded p-1 text-gray-400 disabled:opacity-30"><ChevronUp size={14} /></button>
        <button type="button" aria-label={`Move ${filter.type} filter down`} disabled={index === filters.length - 1} onClick={() => move(index, 1)} className="rounded p-1 text-gray-400 disabled:opacity-30"><ChevronDown size={14} /></button>
      </div>
    </div>)}
  </div>;
}
